import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import {
  Plus, MoreHorizontal, Edit, Trash2, XCircle, Printer, ChevronsUpDown, Check,
  FileText, Upload, X, Download, File, FileSpreadsheet, FileJson, FileType, Eye
} from "lucide-react";
import generarReciboPdf from "./reciboPdfGenerator";

const API = process.env.REACT_APP_BACKEND_URL;

const ComprobantesPago = ({ portalMode = false }) => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  
  // Get user info for portal mode
  const [portalUser, setPortalUser] = useState(null);
  const isPortal = portalMode;

  useEffect(() => {
    if (isPortal) {
      axios.get(`${API}/api/auth/me`, { headers }).then(res => setPortalUser(res.data)).catch(() => {});
    }
  }, [isPortal]);

  // Data states
  const [data, setData] = useState([]);
  const [columnFilters, setColumnFilters] = useState({
    nro_recibo: "", rif: "", nombre: "", fecha: "", tramite: "", banco: "", oficina: "", monto: "", estatus: ""
  });

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  // Dropdowns
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [contribuyentes, setContribuyentes] = useState([]);
  const [tramitesPopoverOpen, setTramitesPopoverOpen] = useState(false);
  const [tramitesPendientes, setTramitesPendientes] = useState([]);
  const [bancosPopoverOpen, setBancosPopoverOpen] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [cuentasRecaudadoras, setCuentasRecaudadoras] = useState([]);
  const [initialEditLoad, setInitialEditLoad] = useState(false);

  // PDF Preview
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState("");

  // Fetch functions
  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/api/recaudaciones/comprobantes-pago`, { headers });
      setData(response.data);
    } catch { toast.error("Error al cargar datos"); }
  };

  const fetchContribuyentes = async (clase) => {
    try {
      const claseParam = clase === "Contribuyente con LAE" ? "con-lae" : "sin-lae";
      const response = await axios.get(`${API}/api/contribuyentes/buscar?clase=${claseParam}`, { headers });
      setContribuyentes(response.data);
    } catch { toast.error("Error al cargar contribuyentes"); }
  };

  const fetchTramitesPendientes = async (contribuyenteId, rifContrib) => {
    try {
      let url = `${API}/api/recaudaciones/tramites-pendientes`;
      const params = [];
      if (contribuyenteId) params.push(`contribuyente_id=${contribuyenteId}`);
      if (rifContrib) params.push(`rif=${rifContrib}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const response = await axios.get(url, { headers });
      setTramitesPendientes(response.data);
    } catch { toast.error("Error al cargar trámites pendientes"); }
  };

  const fetchBancos = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/bancos`, { headers });
      setBancos(response.data);
    } catch {}
  };

  const fetchCuentasRecaudadoras = async () => {
    try {
      const response = await axios.get(`${API}/api/definiciones/cuentas-bancarias`, { headers });
      const recaudadoras = response.data.filter(c => c.tipo_cuenta === "Recaudadora" || c.es_recaudadora);
      setCuentasRecaudadoras(recaudadoras.length > 0 ? recaudadoras : response.data);
    } catch {}
  };

  useEffect(() => { fetchData(); fetchBancos(); fetchCuentasRecaudadoras(); }, []);

  useEffect(() => {
    if (formData.clase_contribuyente) {
      if (!initialEditLoad && !isPortal) {
        setFormData(prev => ({ ...prev, rif: "", contribuyente_id: "", nombre_razon_social: "" }));
        setTramitesPendientes([]);
      } else {
        setInitialEditLoad(false);
      }
      fetchContribuyentes(formData.clase_contribuyente);
    }
  }, [formData.clase_contribuyente]);

  useEffect(() => {
    if (formData.contribuyente_id) {
      fetchTramitesPendientes(formData.contribuyente_id, formData.rif);
    }
  }, [formData.contribuyente_id]);

  // CRUD
  const handleCreate = async () => {
    // Validación obligatoria para portal
    if (isPortal) {
      if (!formData.tramite_por_pagar_id) { toast.error("Debe seleccionar un Trámite por Pagar"); return; }
      if (!formData.banco_emisor_id) { toast.error("Debe seleccionar un Banco Emisor"); return; }
      if (!formData.cuenta_recaudadora_id) { toast.error("Debe seleccionar una Cuenta Recaudadora"); return; }
      if (!formData.nro_referencia) { toast.error("Debe ingresar el Nro. de Referencia"); return; }
      if (!formData.monto || formData.monto <= 0) { toast.error("El Monto debe ser mayor a 0"); return; }
      if (!formData.comprobante_pago_url) { toast.error("Debe cargar el Comprobante de Pago"); return; }
      // Validar monto vs planilla
      if (formData.monto_planilla && Math.abs(formData.monto - formData.monto_planilla) > 0.01) {
        toast.warning(`El monto ingresado (${formData.monto.toLocaleString('es-VE', {minimumFractionDigits: 2})} Bs.) no coincide con el monto de la planilla (${formData.monto_planilla.toLocaleString('es-VE', {minimumFractionDigits: 2})} Bs.)`);
        return;
      }
    }
    
    // Limpiar campo auxiliar antes de enviar
    const dataToSend = { ...formData };
    delete dataToSend.monto_planilla;
    
    try {
      await axios.post(`${API}/api/recaudaciones/comprobantes-pago`, dataToSend, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false); setFormData({}); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al crear registro"); }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/recaudaciones/comprobantes-pago/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false); setEditingItem(null); setFormData({}); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al actualizar registro"); }
  };

  const handleAnular = async (id) => {
    if (!window.confirm("¿Está seguro de anular este comprobante?")) return;
    try {
      await axios.put(`${API}/api/recaudaciones/comprobantes-pago/${id}`, { estatus: "Anulado" }, { headers });
      toast.success("Comprobante anulado exitosamente");
      fetchData();
    } catch { toast.error("Error al anular comprobante"); }
  };

  const openCreateDialog = async () => {
    setEditingItem(null);
    const defaults = { fecha_recaudacion: new Date().toISOString().split('T')[0], estatus: "Pendiente", monto: 0 };
    
    if (isPortal) {
      // Obtener datos del usuario si aún no se cargó
      let userData = portalUser;
      if (!userData) {
        try {
          const res = await axios.get(`${API}/api/auth/me`, { headers });
          userData = res.data;
          setPortalUser(userData);
        } catch {}
      }
      
      if (userData) {
        const clase = userData.contribuyente_tipo === "con-lae" ? "Contribuyente con LAE" : "Contribuyente sin LAE";
        defaults.clase_contribuyente = clase;
        defaults.rif = userData.contribuyente_rif || "";
        defaults.nombre_razon_social = userData.nombre || "";
        defaults.oficina = "Hacienda";
        
        try {
          const claseParam = userData.contribuyente_tipo === "con-lae" ? "con-lae" : "sin-lae";
          const res = await axios.get(`${API}/api/contribuyentes/buscar?clase=${claseParam}`, { headers });
          setContribuyentes(res.data);
          const matched = res.data.find(c => `${c.tipo_rif}-${c.rif}` === userData.contribuyente_rif);
          if (matched) {
            defaults.contribuyente_id = matched.id;
            defaults.nombre_razon_social = matched.nombre;
            defaults.rif = `${matched.tipo_rif}-${matched.rif}`;
            fetchTramitesPendientes(matched.id, userData.contribuyente_rif);
          }
        } catch {}
      }
    }
    
    setFormData(defaults);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setInitialEditLoad(true);
    setFormData(item);
    if (item.clase_contribuyente) fetchContribuyentes(item.clase_contribuyente);
    if (item.contribuyente_id) fetchTramitesPendientes(item.contribuyente_id, item.rif);
    setDialogOpen(true);
  };

  // PDF
  const handlePrintRecibo = async (comprobante) => {
    try {
      toast.info("Generando recibo...");
      const { pdfUrl, pdfFileName: name, pdfDoc } = await generarReciboPdf(comprobante, headers);
      setPdfPreviewUrl(pdfUrl);
      setPdfFileName(name);
      setPdfPreviewOpen(true);
      window._currentPdfDoc = pdfDoc;
    } catch (error) { toast.error(`Error al generar el recibo: ${error.message || "Error desconocido"}`); }
  };

  const handleDownloadPdf = () => {
    if (window._currentPdfDoc && pdfFileName) {
      window._currentPdfDoc.save(pdfFileName);
      toast.success("Recibo descargado exitosamente");
    }
  };

  const handleClosePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewOpen(false); setPdfPreviewUrl(null); setPdfFileName("");
    window._currentPdfDoc = null;
  };

  // Filtering
  const filteredData = data.filter(item => {
    const cf = columnFilters;
    if (cf.nro_recibo && !(item.nro_recibo || "").toLowerCase().includes(cf.nro_recibo.toLowerCase())) return false;
    if (cf.rif && !(item.rif || "").toLowerCase().includes(cf.rif.toLowerCase())) return false;
    if (cf.nombre && !(item.nombre_razon_social || "").toLowerCase().includes(cf.nombre.toLowerCase())) return false;
    if (cf.fecha && !(item.fecha_recaudacion || "").includes(cf.fecha)) return false;
    if (cf.tramite && !(item.tramite_por_pagar_nro || "").toLowerCase().includes(cf.tramite.toLowerCase())) return false;
    if (cf.banco && !(item.banco_emisor_nombre || "").toLowerCase().includes(cf.banco.toLowerCase())) return false;
    if (cf.oficina && !(item.oficina || "").toLowerCase().includes(cf.oficina.toLowerCase())) return false;
    if (cf.monto && !String(item.monto || "").includes(cf.monto)) return false;
    if (cf.estatus && cf.estatus !== "todos" && item.estatus !== cf.estatus) return false;
    return true;
  }).sort((a, b) => (b.nro_recibo || "").localeCompare(a.nro_recibo || "", undefined, { numeric: true }));

  // Export functions
  const getExportData = () => filteredData.map(item => ({
    "Nro. Recibo": item.nro_recibo || "", "RIF": item.rif || "",
    "Nombre / Razón Social": item.nombre_razon_social || "", "Fecha": item.fecha_recaudacion || "",
    "Trámite": item.tramite_por_pagar_nro || "", "Banco": item.banco_emisor_nombre || "",
    "Oficina": item.oficina || "", "Monto": item.monto || 0, "Estatus": item.estatus || ""
  }));

  const exportToCSV = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    const h = Object.keys(d[0]).join(",");
    const rows = d.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    saveAs(new Blob(["\ufeff" + `${h}\n${rows}`], { type: "text/csv;charset=utf-8" }), `comprobantes_pago_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("Exportado a CSV exitosamente");
  };

  const exportToJSON = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    saveAs(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }), `comprobantes_pago_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("Exportado a JSON exitosamente");
  };

  const exportToExcel = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    const ws = XLSX.utils.json_to_sheet(d);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comprobantes de Pago");
    XLSX.writeFile(wb, `comprobantes_pago_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Exportado a Excel exitosamente");
  };

  const exportToPDF = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    const doc = new jsPDF('landscape');
    doc.setFontSize(16); doc.text("Comprobantes de Pago", 14, 20);
    doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString('es-VE')}`, 14, 28);
    autoTable(doc, { head: [Object.keys(d[0])], body: d.map(row => Object.values(row)), startY: 35, styles: { fontSize: 8 }, headStyles: { fillColor: [16, 185, 129] } });
    doc.save(`comprobantes_pago_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Exportado a PDF exitosamente");
  };

  const exportToHTML = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    const hdrs = Object.keys(d[0]);
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Comprobantes de Pago</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#10b981}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:12px;text-align:left}th{background-color:#10b981;color:white}tr:nth-child(even){background-color:#f9fafb}tr:hover{background-color:#ecfdf5}.fecha{color:#6b7280;margin-bottom:20px}</style></head><body><h1>Comprobantes de Pago</h1><p class="fecha">Fecha: ${new Date().toLocaleDateString('es-VE')}</p><table><thead><tr>${hdrs.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${d.map(row => `<tr>${Object.values(row).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`;
    saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), `comprobantes_pago_${new Date().toISOString().split('T')[0]}.html`);
    toast.success("Exportado a HTML exitosamente");
  };

  const exportToText = () => {
    const d = getExportData();
    if (!d.length) { toast.error("No hay datos para exportar"); return; }
    const hdrs = Object.keys(d[0]);
    const colWidths = hdrs.map(h => Math.max(h.length, ...d.map(row => String(row[h]).length)));
    let text = `COMPROBANTES DE PAGO\nFecha: ${new Date().toLocaleDateString('es-VE')}\n${"=".repeat(Math.max(colWidths.reduce((a, b) => a + b + 3, 0), 50))}\n\n`;
    text += hdrs.map((h, i) => h.padEnd(colWidths[i])).join(" | ") + "\n";
    text += colWidths.map(w => "-".repeat(w)).join("-+-") + "\n";
    d.forEach(row => { text += hdrs.map((h, i) => String(row[h]).padEnd(colWidths[i])).join(" | ") + "\n"; });
    saveAs(new Blob([text], { type: "text/plain;charset=utf-8" }), `comprobantes_pago_${new Date().toISOString().split('T')[0]}.txt`);
    toast.success("Exportado a Texto exitosamente");
  };

  // Preview comprobante
  const [comprobantePreviewOpen, setComprobantePreviewOpen] = useState(false);
  const [comprobantePreviewUrl, setComprobantePreviewUrl] = useState(null);

  const clasesContribuyente = ["Contribuyente con LAE", "Contribuyente sin LAE"];

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        {!isPortal && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="btn-exportar"><Download className="w-4 h-4" />Exportar</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" />Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" />PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" />Texto</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" />HTML</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" />CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" />JSON</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
        <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-nuevo"><Plus className="w-4 h-4 mr-2" />Nuevo Comprobante</Button>
      </div>

      {/* Data table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Nro. Recibo</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">RIF</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Nombre / Razón Social</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Fecha</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Trámite</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Banco</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Oficina</th>
                  <th className="px-3 py-3 text-right text-sm font-medium text-slate-600">Monto</th>
                  <th className="px-3 py-3 text-left text-sm font-medium text-slate-600">Estatus</th>
                  <th className="px-3 py-3 text-center text-sm font-medium text-slate-600">Acciones</th>
                </tr>
                <tr className="border-b bg-white">
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.nro_recibo} onChange={(e) => setColumnFilters({...columnFilters, nro_recibo: e.target.value})} data-testid="filter-nro-recibo" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.rif} onChange={(e) => setColumnFilters({...columnFilters, rif: e.target.value})} data-testid="filter-rif" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.nombre} onChange={(e) => setColumnFilters({...columnFilters, nombre: e.target.value})} data-testid="filter-nombre" /></th>
                  <th className="px-3 py-1.5"><Input type="date" className="h-7 text-xs" value={columnFilters.fecha} onChange={(e) => setColumnFilters({...columnFilters, fecha: e.target.value})} data-testid="filter-fecha" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.tramite} onChange={(e) => setColumnFilters({...columnFilters, tramite: e.target.value})} data-testid="filter-tramite" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.banco} onChange={(e) => setColumnFilters({...columnFilters, banco: e.target.value})} data-testid="filter-banco" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={columnFilters.oficina} onChange={(e) => setColumnFilters({...columnFilters, oficina: e.target.value})} data-testid="filter-oficina" /></th>
                  <th className="px-3 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={columnFilters.monto} onChange={(e) => setColumnFilters({...columnFilters, monto: e.target.value})} data-testid="filter-monto" /></th>
                  <th className="px-3 py-1.5">
                    <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={columnFilters.estatus} onChange={(e) => setColumnFilters({...columnFilters, estatus: e.target.value})} data-testid="filter-estatus">
                      <option value="">Todos</option><option value="Pagado">Pagado</option><option value="Pendiente">Pendiente</option><option value="Anulado">Anulado</option>
                    </select>
                  </th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-500">No hay comprobantes de pago registrados</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.nro_recibo || <span className="text-amber-500 text-xs">Pendiente</span>}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.rif}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_razon_social}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.fecha_recaudacion || item.fecha || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.tramite_por_pagar_nro || item.nro_planilla || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.banco_emisor_nombre}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.oficina || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900 text-right font-medium">{item.monto?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.estatus === "Pagado" ? "bg-green-100 text-green-800" : item.estatus === "Anulado" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{item.estatus}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(item)}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrintRecibo(item)}><Printer className="w-4 h-4 mr-2" />Imprimir</DropdownMenuItem>
                            {!isPortal && (
                            <DropdownMenuItem onClick={() => handleAnular(item.id)} className="text-red-600"><XCircle className="w-4 h-4 mr-2" />Anular</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? (isPortal ? "Editar Reporte de Pago" : "Editar Comprobante de Pago") : (isPortal ? "Nuevo Reporte de Pago" : "Nuevo Comprobante de Pago")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {/* Clase de Contribuyente */}
            <div className="space-y-2">
              <Label>Clase de Contribuyente</Label>
              {isPortal ? (
                <Input value={formData.clase_contribuyente || ""} disabled className="bg-slate-100" data-testid="form-clase" />
              ) : (
              <Select value={formData.clase_contribuyente || ""} onValueChange={(value) => setFormData({...formData, clase_contribuyente: value})}>
                <SelectTrigger data-testid="form-clase"><SelectValue placeholder="Seleccione clase" /></SelectTrigger>
                <SelectContent>{clasesContribuyente.map((clase) => (<SelectItem key={clase} value={clase}>{clase}</SelectItem>))}</SelectContent>
              </Select>
              )}
            </div>

            {/* RIF / Contribuyente */}
            <div className="space-y-2">
              <Label>RIF / Contribuyente</Label>
              {isPortal ? (
                <Input value={formData.rif ? `${formData.rif} - ${formData.nombre_razon_social || ""}` : ""} disabled className="bg-slate-100" data-testid="form-rif" />
              ) : (
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={rifPopoverOpen} className="w-full justify-between h-10 font-normal" disabled={!formData.clase_contribuyente} data-testid="form-rif">
                    {formData.rif ? (
                      <span className="truncate">{contribuyentes.find(c => c.id === formData.contribuyente_id)?.display_name || (formData.nombre_razon_social ? `${formData.rif} - ${formData.nombre_razon_social}` : formData.rif)}</span>
                    ) : (
                      <span className="text-slate-500">{formData.clase_contribuyente ? "Seleccione contribuyente..." : "Primero seleccione clase"}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por RIF o nombre..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron contribuyentes</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.map((contribuyente) => (
                          <CommandItem key={contribuyente.id} value={contribuyente.display_name} onSelect={() => {
                            setFormData({ ...formData, rif: `${contribuyente.tipo_rif}-${contribuyente.rif}`, contribuyente_id: contribuyente.id, nombre_razon_social: contribuyente.nombre || contribuyente.nombre_razon_social || contribuyente.razon_social });
                            setRifPopoverOpen(false);
                          }}>
                            <Check className={`mr-2 h-4 w-4 ${formData.contribuyente_id === contribuyente.id ? "opacity-100" : "opacity-0"}`} />
                            {contribuyente.display_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              )}
            </div>

            {/* Fecha de Recaudación */}
            <div className="space-y-2">
              <Label>Fecha de Recaudación</Label>
              <Input type="date" value={formData.fecha_recaudacion || ""} onChange={(e) => setFormData({...formData, fecha_recaudacion: e.target.value})} disabled={isPortal} className={isPortal ? "bg-slate-100" : ""} data-testid="form-fecha" />
            </div>

            {/* Trámite por Pagar */}
            <div className="space-y-2">
              <Label>Trámite por Pagar</Label>
              <Popover open={tramitesPopoverOpen} onOpenChange={setTramitesPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={tramitesPopoverOpen} className="w-full justify-between h-10 font-normal" disabled={!formData.contribuyente_id} data-testid="form-tramite">
                    {formData.tramite_por_pagar_nro ? (<span className="truncate">Planilla #{formData.tramite_por_pagar_nro}</span>) : (<span className="text-slate-500">{formData.contribuyente_id ? "Seleccione trámite..." : "Primero seleccione contribuyente"}</span>)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar planilla..." />
                    <CommandList>
                      <CommandEmpty>No hay trámites pendientes</CommandEmpty>
                      <CommandGroup>
                        {tramitesPendientes.map((tramite) => (
                          <CommandItem key={tramite.id} value={`Planilla #${tramite.nro_planilla} - ${tramite.total?.toLocaleString('es-VE')} Bs`} onSelect={() => {
                            setFormData({ ...formData, tramite_por_pagar_id: tramite.id, tramite_por_pagar_nro: tramite.nro_planilla, monto: tramite.total || 0, monto_planilla: tramite.total || 0, oficina: tramite.oficina_nombre || tramite.oficina || "", rif: tramite.rif || formData.rif, nombre_razon_social: tramite.nombre_razon_social || formData.nombre_razon_social, contribuyente_id: tramite.contribuyente_id || formData.contribuyente_id });
                            setTramitesPopoverOpen(false);
                          }}>
                            <Check className={`mr-2 h-4 w-4 ${formData.tramite_por_pagar_id === tramite.id ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex flex-col">
                              <span>Planilla #{tramite.nro_planilla}</span>
                              <span className="text-xs text-slate-500">Total: {tramite.total?.toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs{tramite.oficina_nombre && ` | ${tramite.oficina_nombre}`}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Oficina */}
            <div className="space-y-2">
              <Label>Oficina</Label>
              <div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700 cursor-not-allowed" data-testid="form-oficina">{formData.oficina || "Se cargará al seleccionar trámite"}</div>
            </div>

            {/* Banco Emisor */}
            <div className="space-y-2">
              <Label>Banco Emisor</Label>
              <Popover open={bancosPopoverOpen} onOpenChange={setBancosPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={bancosPopoverOpen} className="w-full justify-between h-10 font-normal" data-testid="form-banco">
                    {formData.banco_emisor_nombre ? (<span className="truncate">{formData.banco_emisor_nombre}</span>) : (<span className="text-slate-500">Seleccione banco...</span>)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar banco..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron bancos</CommandEmpty>
                      <CommandGroup>
                        {bancos.map((banco) => (
                          <CommandItem key={banco.id} value={banco.nombre} onSelect={() => { setFormData({ ...formData, banco_emisor_id: banco.id, banco_emisor_nombre: banco.nombre }); setBancosPopoverOpen(false); }}>
                            <Check className={`mr-2 h-4 w-4 ${formData.banco_emisor_id === banco.id ? "opacity-100" : "opacity-0"}`} />{banco.nombre}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Cuenta Recaudadora */}
            <div className="space-y-2">
              <Label>Cuenta Recaudadora</Label>
              <Select value={formData.cuenta_recaudadora_id || ""} onValueChange={(value) => {
                const cuenta = cuentasRecaudadoras.find(c => c.id === value);
                setFormData({ ...formData, cuenta_recaudadora_id: value, cuenta_recaudadora_numero: cuenta?.numero_cuenta || "", cuenta_recaudadora_descripcion: cuenta?.descripcion || "" });
              }}>
                <SelectTrigger data-testid="form-cuenta"><SelectValue placeholder="Seleccione cuenta" /></SelectTrigger>
                <SelectContent>{cuentasRecaudadoras.map((cuenta) => (<SelectItem key={cuenta.id} value={cuenta.id}>{cuenta.descripcion} - {cuenta.numero_cuenta}</SelectItem>))}</SelectContent>
              </Select>
            </div>

            {/* Nro. de Referencia */}
            <div className="space-y-2">
              <Label>Nro. de Referencia</Label>
              <Input value={formData.nro_referencia || ""} onChange={(e) => setFormData({...formData, nro_referencia: e.target.value})} placeholder="Número de referencia bancaria" data-testid="form-referencia" />
            </div>

            {/* Monto */}
            <div className="space-y-2">
              <Label>Monto</Label>
              <Input type="number" step="0.01" value={formData.monto || ""} onChange={(e) => setFormData({...formData, monto: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid="form-monto" />
            </div>

            {/* Comprobante de Pago (Imagen) */}
            <div className="space-y-2 md:col-span-2">
              <Label>Comprobante de Pago</Label>
              {formData.comprobante_pago_url ? (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      {formData.comprobante_pago_url.startsWith('data:image') ? (
                        <img src={formData.comprobante_pago_url} alt="Comprobante de pago" className="max-h-48 rounded-lg border shadow-sm" />
                      ) : formData.comprobante_pago_url.startsWith('data:application/pdf') ? (
                        <div className="flex items-center gap-2 p-4 bg-white rounded-lg border"><FileText className="w-8 h-8 text-red-500" /><span className="text-sm text-slate-600">Documento PDF cargado</span></div>
                      ) : (
                        <div className="flex items-center gap-2 p-4 bg-white rounded-lg border"><FileText className="w-8 h-8 text-slate-400" /><span className="text-sm text-slate-600">Archivo cargado</span></div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setComprobantePreviewUrl(formData.comprobante_pago_url); setComprobantePreviewOpen(true); }} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid="btn-ver-comprobante"><Eye className="w-4 h-4 mr-1" />Ver</Button>
                      <Button variant="outline" size="sm" onClick={() => setFormData({...formData, comprobante_pago_url: ""})} className="text-red-600 hover:text-red-700 hover:bg-red-50"><X className="w-4 h-4 mr-1" />Eliminar</Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo es muy grande. Máximo 5MB"); return; }
                    try {
                      const formDataUpload = new FormData();
                      formDataUpload.append('file', file);
                      const response = await axios.post(`${API}/api/recaudaciones/upload-comprobante`, formDataUpload, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                      setFormData(prev => ({...prev, comprobante_pago_url: response.data.url}));
                      toast.success("Comprobante cargado exitosamente");
                    } catch (error) { toast.error(error.response?.data?.detail || "Error al cargar el comprobante"); }
                  }} className="hidden" id="comprobante-upload" data-testid="form-comprobante-input" />
                  <label htmlFor="comprobante-upload" className="cursor-pointer flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center"><Upload className="w-6 h-6 text-emerald-600" /></div>
                    <span className="text-sm font-medium text-slate-700">Haga clic para cargar el comprobante</span>
                    <span className="text-xs text-slate-500">JPG, PNG, GIF, WEBP o PDF (máx. 5MB)</span>
                  </label>
                </div>
              )}
            </div>

            {/* Estatus */}
            <div className="space-y-2">
              <Label>Estatus</Label>
              {isPortal ? (
                <Input value="Pendiente" disabled className="bg-slate-100" data-testid="form-estatus" />
              ) : (
              <Select value={formData.estatus || "Pendiente"} onValueChange={(value) => setFormData({...formData, estatus: value})}>
                <SelectTrigger data-testid="form-estatus"><SelectValue placeholder="Seleccione estatus" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendiente">Pendiente</SelectItem>
                  <SelectItem value="Pagado">Pagado</SelectItem>
                  <SelectItem value="Anulado">Anulado</SelectItem>
                </SelectContent>
              </Select>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Previsualización de PDF */}
      <Dialog open={pdfPreviewOpen} onOpenChange={handleClosePdfPreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="pdf-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Previsualización del Recibo</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-gray-100 rounded-lg overflow-hidden">
            {pdfPreviewUrl && <iframe src={pdfPreviewUrl} className="w-full h-full border-0" title="Previsualización PDF" data-testid="pdf-preview-iframe" />}
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleClosePdfPreview} data-testid="pdf-preview-cancel-btn"><X className="w-4 h-4 mr-2" />Cerrar</Button>
            <Button onClick={handleDownloadPdf} className="bg-green-600 hover:bg-green-700" data-testid="pdf-preview-download-btn"><Printer className="w-4 h-4 mr-2" />Descargar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Previsualización del Comprobante de Pago */}
      <Dialog open={comprobantePreviewOpen} onOpenChange={setComprobantePreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="comprobante-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" />Comprobante de Pago</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center bg-slate-100 rounded-lg p-4">
            {comprobantePreviewUrl && (
              comprobantePreviewUrl.startsWith('data:application/pdf') ? (
                <iframe src={comprobantePreviewUrl} className="w-full h-[70vh] border-0" title="Comprobante PDF" />
              ) : (
                <img src={comprobantePreviewUrl} alt="Comprobante de pago" className="max-w-full max-h-[70vh] rounded-lg shadow-md" />
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComprobantePreviewOpen(false)}><X className="w-4 h-4 mr-2" />Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComprobantesPago;
